import { useSectionAccess } from "../../../auth/useSectionAccess";
import { FormEvent, useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { TableSkeleton } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import {
  carriersApi,
  type CarrierView,
  type DriverView,
  type VehicleKind,
  type VehicleView,
} from "../../../api/transport";
import type { ApiError } from "../../../api/client";
import { useToast } from "../../../components/Toast";
import { useConfirm } from "../../../components/ConfirmDialog";
import { Select } from "../../../components/Select";
import Ic from "../../../components/icons.jsx";

const VEHICLE_KINDS: VehicleKind[] = ["TRUCK", "TRAILER"];

/**
 * Carrier / vehicle / driver codebooks. A carrier list up top; selecting one opens its fleet below —
 * vehicles and drivers both belong to a carrier, never to Bankom directly.
 *
 * A trailer is a `VehicleView` with `kind: "TRAILER"`, not a separate entity —
 * it is swapped independently of the tractor unit at the yard, so it needs
 * its own plate and its own row, but the codebook and the CRUD are shared
 * with trucks (see `TripsPage`'s "Plan a trip" form, which splits the same
 * list by `kind` for the vehicle/trailer pickers).
 *
 * Deactivation is a soft delete on the backend: `DELETE` sets `deletedAt`,
 * which drops the row out of every list endpoint above for good — there is
 * no "reactivate" once that happens (the update routes 404 the same way the
 * reads do). The UI names the action "Deactivate" throughout, never
 * "Delete", to match — a carrier already referenced by a trip or a delivery
 * keeps its meaning years later, it just stops being offered for new ones.
 */
export default function CarriersPage() {
  const { canWrite, canFull } = useSectionAccess("carriers");
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [carriers, setCarriers] = useState<CarrierView[] | null>(null);
  const [vehicles, setVehicles] = useState<VehicleView[]>([]);
  const [drivers, setDrivers] = useState<DriverView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [carrierForm, setCarrierForm] = useState<CarrierView | "new" | null>(null);
  const [vehicleForm, setVehicleForm] = useState<VehicleView | "new" | null>(null);
  const [driverForm, setDriverForm] = useState<DriverView | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, v, d] = await Promise.all([
        carriersApi.list(),
        carriersApi.listVehicles(),
        carriersApi.listDrivers(),
      ]);
      setCarriers(c);
      setVehicles(v);
      setDrivers(d);
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = carriers?.find((c) => c.id === selectedId) ?? null;
  const fleetVehicles = selected ? vehicles.filter((v) => v.carrierId === selected.id) : [];
  const fleetDrivers = selected ? drivers.filter((d) => d.carrierId === selected.id) : [];

  async function deactivateCarrier(c: CarrierView) {
    if (
      !(await confirm({
        message: t("admin.transport.carriers.confirmDeactivate", {
          name: c.name,
          defaultValue:
            'Deactivate "{{name}}"? It disappears from every picker; past trips and deliveries keep the record.',
        }),
        danger: true,
      }))
    )
      return;
    try {
      await carriersApi.deactivate(c.id);
      if (selectedId === c.id) setSelectedId(null);
      await load();
      toast.success(t("admin.transport.carriers.deactivated", "Carrier deactivated."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function deactivateVehicle(v: VehicleView) {
    if (
      !(await confirm({
        message: t("admin.transport.vehicles.confirmDeactivate", {
          plate: v.plate,
          defaultValue: 'Deactivate "{{plate}}"? It disappears from every picker; past trips keep the record.',
        }),
        danger: true,
      }))
    )
      return;
    try {
      await carriersApi.deactivateVehicle(v.id);
      await load();
      toast.success(t("admin.transport.vehicles.deactivated", "Vehicle deactivated."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function deactivateDriver(d: DriverView) {
    if (
      !(await confirm({
        message: t("admin.transport.drivers.confirmDeactivate", {
          name: d.fullName,
          defaultValue: 'Deactivate "{{name}}"? It disappears from every picker; past trips keep the record.',
        }),
        danger: true,
      }))
    )
      return;
    try {
      await carriersApi.deactivateDriver(d.id);
      await load();
      toast.success(t("admin.transport.drivers.deactivated", "Driver deactivated."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // `--danger` isn't defined in styles.css (several other admin pages copy this
  // same pattern and inherit the same miss) — `--err` is the token actually
  // declared for error-colored text.
  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("admin.transport.carriers.title", "Carriers")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t("admin.transport.carriers.subtitle", "Hauliers, their trucks and trailers, and their drivers.")}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setCarrierForm("new")}>
            <Ic.plus /> {t("admin.transport.carriers.new", "New carrier")}
          </button>
        </div>
      </div>

      {!carriers ? (
        <TableSkeleton rows={6} cols={4} label={t("common.loading")} />
      ) : carriers.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.transport.carriers.empty", "No carriers yet.")}</p>
      ) : (
        <table className="list-card">
          <thead>
            <tr>
              <th style={th}>{t("admin.transport.carriers.col.name", "Name")}</th>
              <th style={th}>{t("admin.transport.carriers.col.taxId", "Tax ID")}</th>
              <th style={th}>{t("admin.transport.carriers.col.phone", "Phone")}</th>
              <th style={th}>{t("admin.transport.carriers.col.email", "Email")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {carriers.map((c) => (
              <tr key={c.id} style={{ background: c.id === selectedId ? "var(--surface-2)" : undefined }}>
                <td style={td} data-label={t("admin.transport.carriers.col.name", "Name")}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id === selectedId ? null : c.id)}
                    style={linkBtn}
                  >
                    {c.name}
                  </button>
                </td>
                <td style={td} data-label={t("admin.transport.carriers.col.taxId", "Tax ID")}>{c.taxId ?? "—"}</td>
                <td style={td} data-label={t("admin.transport.carriers.col.phone", "Phone")}>{c.phone ?? "—"}</td>
                <td style={td} data-label={t("admin.transport.carriers.col.email", "Email")}>{c.email ?? "—"}</td>
                <td style={{ ...td, textAlign: "right" }} className="card-actions" data-label={t("common.actions", "Actions")}>
                  <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setCarrierForm(c)}>
                    {t("action.edit", "Edit")}
                  </button>
                  <button type="button" className="btn sm" disabled={!canFull} onClick={() => void deactivateCarrier(c)}>
                    {t("action.deactivate", "Deactivate")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <section style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h2 style={cardTitle}>
              {t("admin.transport.carriers.fleetTitle", "{{name}} — fleet", { name: selected.name })}
            </h2>
            <button type="button" className="btn sm" onClick={() => setSelectedId(null)}>
              {t("admin.transport.carriers.close", "Close")}
            </button>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={subTitle}>{t("admin.transport.vehicles.title", "Vehicles")}</h3>
              <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setVehicleForm("new")}>
                <Ic.plus /> {t("admin.transport.vehicles.new", "New vehicle")}
              </button>
            </div>
            {fleetVehicles.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>
                {t("admin.transport.vehicles.empty", "No vehicles yet.")}
              </p>
            ) : (
              <table className="list-card">
                <thead>
                  <tr>
                    <th style={th}>{t("admin.transport.vehicles.col.plate", "Plate")}</th>
                    <th style={th}>{t("admin.transport.vehicles.col.kind", "Kind")}</th>
                    <th style={th}>{t("admin.transport.vehicles.col.capacity", "Capacity (kg)")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fleetVehicles.map((v) => (
                    <tr key={v.id}>
                      <td style={{ ...td, fontFamily: "var(--mono)" }} data-label={t("admin.transport.vehicles.col.plate", "Plate")}>
                        {v.plate}
                      </td>
                      <td style={td} data-label={t("admin.transport.vehicles.col.kind", "Kind")}>
                        {t(`admin.transport.vehicles.kind.${v.kind}`, v.kind)}
                      </td>
                      <td style={td} data-label={t("admin.transport.vehicles.col.capacity", "Capacity (kg)")}>
                        {v.capacityKg ?? "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right" }} className="card-actions" data-label={t("common.actions", "Actions")}>
                        <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setVehicleForm(v)}>
                          {t("action.edit", "Edit")}
                        </button>
                        <button type="button" className="btn sm" disabled={!canFull} onClick={() => void deactivateVehicle(v)}>
                          {t("action.deactivate", "Deactivate")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={subTitle}>{t("admin.transport.drivers.title", "Drivers")}</h3>
              <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setDriverForm("new")}>
                <Ic.plus /> {t("admin.transport.drivers.new", "New driver")}
              </button>
            </div>
            {fleetDrivers.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>
                {t("admin.transport.drivers.empty", "No drivers yet.")}
              </p>
            ) : (
              <table className="list-card">
                <thead>
                  <tr>
                    <th style={th}>{t("admin.transport.drivers.col.fullName", "Full name")}</th>
                    <th style={th}>{t("admin.transport.drivers.col.phone", "Phone")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fleetDrivers.map((d) => (
                    <tr key={d.id}>
                      <td style={td} data-label={t("admin.transport.drivers.col.fullName", "Full name")}>{d.fullName}</td>
                      <td style={td} data-label={t("admin.transport.drivers.col.phone", "Phone")}>{d.phone ?? "—"}</td>
                      <td style={{ ...td, textAlign: "right" }} className="card-actions" data-label={t("common.actions", "Actions")}>
                        <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setDriverForm(d)}>
                          {t("action.edit", "Edit")}
                        </button>
                        <button type="button" className="btn sm" disabled={!canFull} onClick={() => void deactivateDriver(d)}>
                          {t("action.deactivate", "Deactivate")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {canWrite && carrierForm && (
        <CarrierFormDialog
          carrier={carrierForm === "new" ? null : carrierForm}
          onClose={() => setCarrierForm(null)}
          onSaved={async () => {
            setCarrierForm(null);
            await load();
          }}
        />
      )}
      {canWrite && vehicleForm && selected && (
        <VehicleFormDialog
          carrierId={selected.id}
          vehicle={vehicleForm === "new" ? null : vehicleForm}
          onClose={() => setVehicleForm(null)}
          onSaved={async () => {
            setVehicleForm(null);
            await load();
          }}
        />
      )}
      {canWrite && driverForm && selected && (
        <DriverFormDialog
          carrierId={selected.id}
          driver={driverForm === "new" ? null : driverForm}
          onClose={() => setDriverForm(null)}
          onSaved={async () => {
            setDriverForm(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/** Create/edit form for a carrier. The backend refuses a duplicate name
 * (case-insensitively) with 409 `CARRIER_EXISTS` — the ordinary path when
 * someone re-registers a haulier already on file, surfaced inline rather
 * than as a generic toast. */
function CarrierFormDialog({
  carrier, onClose, onSaved,
}: {
  carrier: CarrierView | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(carrier?.name ?? "");
  const [taxId, setTaxId] = useState(carrier?.taxId ?? "");
  const [phone, setPhone] = useState(carrier?.phone ?? "");
  const [email, setEmail] = useState(carrier?.email ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = {
        name: name.trim(),
        taxId: taxId.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      };
      if (carrier) await carriersApi.update(carrier.id, body);
      else await carriersApi.create(body);
      await onSaved();
    } catch (e2) {
      const apiErr = e2 as ApiError;
      setErr(
        apiErr.code === "CARRIER_EXISTS"
          ? t("admin.transport.carriers.error.exists", 'A carrier named "{{name}}" already exists.', { name: name.trim() })
          : apiErr.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {carrier ? t("admin.transport.carriers.editTitle", "Edit carrier") : t("admin.transport.carriers.new", "New carrier")}
        </h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={t("admin.transport.carriers.field.name", "Name")}>
            <input className="input" style={{ width: "100%" }} value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t("admin.transport.carriers.field.taxId", "Tax ID")}>
            <input className="input" style={{ width: "100%" }} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          </Field>
          <Field label={t("admin.transport.carriers.field.phone", "Phone")}>
            <input className="input" style={{ width: "100%" }} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label={t("admin.transport.carriers.field.email", "Email")}>
            <input type="email" className="input" style={{ width: "100%" }} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
              {busy ? "…" : t("common.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Create/edit form for a vehicle (truck or trailer — see the module
 * docstring). The backend refuses a duplicate plate (case-insensitively)
 * with 409 `VEHICLE_PLATE_EXISTS` — the ordinary path when a truck gets
 * re-registered, surfaced inline the same way `CARRIER_EXISTS` is above. */
function VehicleFormDialog({
  carrierId, vehicle, onClose, onSaved,
}: {
  carrierId: string;
  vehicle: VehicleView | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [plate, setPlate] = useState(vehicle?.plate ?? "");
  const [kind, setKind] = useState<VehicleKind>(vehicle?.kind ?? "TRUCK");
  const [capacityKg, setCapacityKg] = useState(vehicle?.capacityKg != null ? String(vehicle.capacityKg) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = {
        plate: plate.trim(),
        kind,
        capacityKg: capacityKg.trim() === "" ? undefined : Number(capacityKg),
      };
      if (vehicle) await carriersApi.updateVehicle(vehicle.id, body);
      else await carriersApi.createVehicle(carrierId, body);
      await onSaved();
    } catch (e2) {
      const apiErr = e2 as ApiError;
      setErr(
        apiErr.code === "VEHICLE_PLATE_EXISTS"
          ? t("admin.transport.vehicles.error.plateExists", 'A vehicle with plate "{{plate}}" already exists.', { plate: plate.trim() })
          : apiErr.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {vehicle ? t("admin.transport.vehicles.editTitle", "Edit vehicle") : t("admin.transport.vehicles.new", "New vehicle")}
        </h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={t("admin.transport.vehicles.field.plate", "Plate")}>
            <input className="input" style={{ width: "100%" }} value={plate} onChange={(e) => setPlate(e.target.value)} required />
          </Field>
          <Field label={t("admin.transport.vehicles.field.kind", "Kind")}>
            <Select<VehicleKind>
              value={kind}
              onChange={setKind}
              ariaLabel={t("admin.transport.vehicles.field.kind", "Kind")}
              style={{ width: "100%" }}
              options={VEHICLE_KINDS.map((k) => ({ value: k, label: t(`admin.transport.vehicles.kind.${k}`, k) }))}
            />
          </Field>
          <Field label={t("admin.transport.vehicles.field.capacity", "Capacity (kg)")}>
            <input
              type="number"
              min="0"
              step="1"
              className="input"
              style={{ width: "100%" }}
              value={capacityKg}
              onChange={(e) => setCapacityKg(e.target.value)}
            />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button type="submit" className="btn primary" disabled={busy || !plate.trim()}>
              {busy ? "…" : t("common.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Create/edit form for a driver. No duplicate check on the backend — two
 * drivers can share a name — so there is no inline error mapping here
 * beyond the generic `ApiError.message` fallback. */
function DriverFormDialog({
  carrierId, driver, onClose, onSaved,
}: {
  carrierId: string;
  driver: DriverView | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(driver?.fullName ?? "");
  const [phone, setPhone] = useState(driver?.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = { fullName: fullName.trim(), phone: phone.trim() || undefined };
      if (driver) await carriersApi.updateDriver(driver.id, body);
      else await carriersApi.createDriver(carrierId, body);
      await onSaved();
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {driver ? t("admin.transport.drivers.editTitle", "Edit driver") : t("admin.transport.drivers.new", "New driver")}
        </h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={t("admin.transport.drivers.field.fullName", "Full name")}>
            <input className="input" style={{ width: "100%" }} value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </Field>
          <Field label={t("admin.transport.drivers.field.phone", "Phone")}>
            <input className="input" style={{ width: "100%" }} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button type="submit" className="btn primary" disabled={busy || !fullName.trim()}>
              {busy ? "…" : t("common.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const th: CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" };
const td: CSSProperties = { padding: "8px 10px", fontSize: 13 };
const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: 18,
  display: "flex", flexDirection: "column", gap: 16,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const subTitle: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink-2)" };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const linkBtn: CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  color: "var(--brand-primary)", font: "inherit", textAlign: "left",
};
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
};
const dialog: CSSProperties = {
  width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};
const errBox: CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
